import React, { FC, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Alert, Button, Segmented, Space, Spin, Tag, Tooltip } from 'antd';
import { useTranslate } from '@/translations';
import type {
  InspectionInsightAgeBand,
  InspectionInsightCardKey,
  InspectionInsightCardMetric,
  InspectionInsightDrill,
  InspectionInsightScope,
  VehicleInspectionInsights,
} from '@/types';
import { faEye, faEyeSlash, Icon } from '@/ui/components';
import {
  axisLabelName,
  chartValue,
  toggleChartLabel,
} from './ageChartSelection';
import {
  CardCount,
  CardDetail,
  CardGrid,
  CardLabel,
  CardNote,
  ChartBlock,
  ChartFrame,
  ChartHeader,
  Charts,
  Controls,
  DrillRow,
  Header,
  Heading,
  Hint,
  InsightCard,
  InsightsPanel,
  LoadingState,
  IntersectionAlert,
  Meta,
  SegmentLabel,
  SegmentSizer,
  SummaryTile,
  SummaryValue,
  Title,
} from './InspectionInsights.styled';

const CARD_ORDER = [
  'Inspected',
  'Required SubCustomer Approval',
  'SubCustomer Audit',
  'SubCustomer Review',
  'Awaiting Authorisation',
  'Requires Parts Pricing',
  'Requires Review',
  'Requires Re-submission Review',
  'Authorised',
  'Authorised - Order Parts',
  'Auth Rejected',
  'Completed',
];

const CARD_RANK: Record<string, number> = {
  inspected: 0,
  Inspected: 0,
  'Required SubCustomer Approval': 1,
  'SubCustomer Audit': 2,
  'SubCustomer Review': 3,
  awaitingAuthorisation: 4,
  'Awaiting Authorisation': 4,
  requiresPartsPricing: 5,
  'Requires Parts Pricing': 5,
  requiresReview: 6,
  'Requires Review': 6,
  requiresResubmissionReview: 7,
  'Requires Re-submission Review': 7,
  authorised: 8,
  Authorised: 8,
  'Authorised - Order Parts': 9,
  'Auth Rejected': 10,
  Completed: 11,
};

const CARD_LABEL: Record<string, string> = {
  inspected: 'Inspected',
  Inspected: 'Inspected',
  awaitingAuthorisation: 'Awaiting Auth.',
  'Awaiting Authorisation': 'Awaiting Auth.',
  requiresPartsPricing: 'Parts Pricing',
  'Requires Parts Pricing': 'Parts Pricing',
  requiresReview: 'Requires Review',
  'Requires Review': 'Requires Review',
  requiresResubmissionReview: 'Re-submission',
  'Requires Re-submission Review': 'Re-submission',
  authorised: 'Authorised',
  Authorised: 'Authorised',
};

const labelFor = (key: string) => CARD_LABEL[key] ?? key;

const weekOnWeekTone = (value: string): 'up' | 'down' | undefined => {
  const amount = Number.parseFloat(value);
  if (!Number.isFinite(amount) || amount === 0) return undefined;
  return amount > 0 ? 'up' : 'down';
};

const AGE_LABEL: Record<InspectionInsightAgeBand, string> = {
  under1d: '< 1d',
  d1to2: '1-2d',
  d2to4: '2-4d',
  d4plus: '4d+',
};

const AGE_COLORS: Record<InspectionInsightAgeBand, string> = {
  under1d: '#60a5fa',
  d1to2: '#38bdf8',
  d2to4: '#fbbf24',
  d4plus: '#f87171',
};

type InspectionInsightsProps = {
  data?: VehicleInspectionInsights;
  loading?: boolean;
  expanded: boolean;
  scope: InspectionInsightScope;
  chartPeriodDays: 7 | 28 | 90;
  drill: InspectionInsightDrill;
  emptyIntersection?: boolean;
  onToggleExpanded: () => void;
  onScopeChange: (scope: InspectionInsightScope) => void;
  onPeriodChange: (days: 7 | 28 | 90) => void;
  onSelectCard: (card: InspectionInsightCardKey) => void;
  onSelectAgeBand: (
    card: InspectionInsightCardKey,
    ageBand: InspectionInsightAgeBand,
  ) => void;
  onClearDrill: () => void;
};

const formatRefresh = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const emptyCard = (
  key: InspectionInsightCardKey,
): InspectionInsightCardMetric => ({
  key,
  count: 0,
  averageAge: null,
  ageAvailable: false,
  unusuallyOldCount: null,
  unusualAgeBaseline: '',
  weekOnWeek: 'unavailable',
  ageBands: {},
});

export const InspectionInsights: FC<InspectionInsightsProps> = ({
  data,
  loading,
  expanded,
  scope,
  chartPeriodDays,
  drill,
  emptyIntersection,
  onToggleExpanded,
  onScopeChange,
  onPeriodChange,
  onSelectCard,
  onSelectAgeBand,
  onClearDrill,
}) => {
  const { t } = useTranslate();
  const [hiddenAgeLabels, setHiddenAgeLabels] = useState<string[]>([]);
  const scopeLabels = [t('insightsAllSite'), t('insightsCurrentResults')];
  const scopeOption = (text: string) => (
    <SegmentLabel>
      <span>{text}</span>
      {scopeLabels.map((label) => (
        <SegmentSizer key={label} aria-hidden>
          {label}
        </SegmentSizer>
      ))}
    </SegmentLabel>
  );
  const cards = useMemo(() => {
    const source = data?.cards ?? [];
    if (source.length === 0) return CARD_ORDER.map((key) => emptyCard(key));

    return [...source].sort(
      (left, right) =>
        (CARD_RANK[left.key] ?? CARD_ORDER.length) -
          (CARD_RANK[right.key] ?? CARD_ORDER.length) ||
        left.key.localeCompare(right.key),
    );
  }, [data]);

  const flowOption = useMemo(() => {
    const points = data?.flow ?? [];
    return {
      tooltip: {
        trigger: 'axis',
        formatter: (
          items: Array<{ axisValue: string; seriesName: string; data: number }>,
        ) => {
          const day = items[0]?.axisValue || '';
          const lines = items.map((item) => `${item.seriesName}: ${item.data}`);
          return [`${day}`, ...lines].join('<br/>');
        },
      },
      legend: { data: [t('insightsInspected'), t('insightsAuthorised')] },
      grid: { left: 8, right: 16, top: 32, bottom: 24, containLabel: true },
      xAxis: {
        type: 'category',
        data: points.map((point) =>
          new Date(point.day).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'short',
          }),
        ),
      },
      yAxis: { type: 'value' },
      series: [
        {
          name: t('insightsInspected'),
          type: 'bar',
          data: points.map((point) => point.inspectedTransitions),
          itemStyle: { color: '#38bdf8' },
        },
        {
          name: t('insightsAuthorised'),
          type: 'bar',
          data: points.map((point) => point.authorisedTransitions),
          itemStyle: { color: '#f87171' },
        },
      ],
    };
  }, [data, t]);

  const ageOption = useMemo(() => {
    const source = data?.ageChart?.length
      ? data.ageChart.map((row) => ({
          key: row.cardKey,
          ageBands: row.ageBands,
          count: row.count,
        }))
      : cards.map((card) => ({
          key: card.key,
          ageBands: card.ageBands,
          count: card.count,
        }));
    const rows = source
      .map((row) => ({
        key: row.key,
        label: labelFor(row.key),
        under1d: row.ageBands?.under1d ?? 0,
        d1to2: row.ageBands?.d1to2 ?? 0,
        d2to4: row.ageBands?.d2to4 ?? 0,
        d4plus: row.ageBands?.d4plus ?? 0,
        count: row.count ?? 0,
      }))
      .reverse();
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: {
        data: Object.values(AGE_LABEL),
      },
      grid: { left: 8, right: 48, top: 32, bottom: 8, containLabel: true },
      xAxis: { type: 'value' },
      yAxis: {
        type: 'category',
        triggerEvent: true,
        data: rows.map((row) => row.label),
        axisLabel: {
          formatter: (value: string) =>
            hiddenAgeLabels.includes(value) ? `{off|${value}}` : value,
          rich: {
            off: { color: '#98a2b3' },
          },
        },
      },
      series: (Object.keys(AGE_LABEL) as InspectionInsightAgeBand[]).map(
        (band) => ({
          name: AGE_LABEL[band],
          type: 'bar',
          stack: 'age',
          data: rows.map((row) =>
            chartValue(row[band], row.label, hiddenAgeLabels),
          ),
          itemStyle: { color: AGE_COLORS[band] },
        }),
      ),
    };
  }, [cards, data, hiddenAgeLabels]);

  return (
    <InsightsPanel data-testid="inspection-insights">
      <Header>
        <Heading>
          <Title>{t('inspectionInsights')}</Title>
          <Meta>
            {data?.siteLabel || t('insightsSelectedSites')}
            {' · '}
            {scope === 'allSite'
              ? t('insightsAllSite')
              : t('insightsCurrentResults')}
            {data?.refreshedAt
              ? ` · ${t('insightsCountsAt')} ${formatRefresh(data.refreshedAt)}`
              : ''}
            {data
              ? ` · ${data.matchingInspectionCount} ${t('insightsInspections')}`
              : ''}
          </Meta>
        </Heading>
        <Controls>
          <Segmented
            block
            value={scope}
            onChange={(value) => onScopeChange(value as InspectionInsightScope)}
            options={[
              { label: scopeOption(t('insightsAllSite')), value: 'allSite' },
              {
                label: scopeOption(t('insightsCurrentResults')),
                value: 'currentResults',
              },
            ]}
          />
          <Button onClick={onToggleExpanded}>
            {expanded ? t('hideInsights') : t('showInsights')}
            <Icon icon={expanded ? faEyeSlash : faEye} />
          </Button>
        </Controls>
      </Header>

      {loading && !data ? (
        <LoadingState data-testid="inspection-insights-loading">
          <Spin />
        </LoadingState>
      ) : (
        <Spin spinning={!!loading}>
          {data?.isEmpty ? (
            <Alert
              type="info"
              showIcon
              message={t('insightsEmptyCurrentResults')}
            />
          ) : (
            <CardGrid $expanded={expanded}>
              {cards.map((card) => (
                <InsightCard
                  key={card.key}
                  type="button"
                  data-testid={`insight-card-${card.key}`}
                  onClick={() => onSelectCard(card.key)}
                  $selected={drill.card === card.key}
                  $expanded={expanded}
                >
                  <CardLabel>{labelFor(card.key)}</CardLabel>
                  <CardCount $expanded={expanded}>{card.count}</CardCount>
                  {expanded ? (
                    <>
                      <CardDetail
                        title={
                          card.ageAvailable
                            ? undefined
                            : t('insightsAgeUnavailable')
                        }
                      >
                        {card.ageAvailable
                          ? `${t('insightsAverage')} ${card.averageAge}`
                          : '-'}
                      </CardDetail>
                      <CardNote
                        $alert={card.unusuallyOldCount != null}
                        data-alert={card.unusuallyOldCount != null || undefined}
                        title={
                          card.unusuallyOldCount == null
                            ? t('insightsUnusualUnavailable')
                            : undefined
                        }
                      >
                        {card.unusuallyOldCount == null
                          ? '-'
                          : `${card.unusuallyOldCount} ${t('insightsUnusuallyOld')}`}
                      </CardNote>
                      <CardDetail
                        $tone={weekOnWeekTone(card.weekOnWeek)}
                        data-tone={weekOnWeekTone(card.weekOnWeek)}
                        title={
                          card.weekOnWeek === 'unavailable'
                            ? t('insightsNoBaseline')
                            : undefined
                        }
                      >
                        {card.weekOnWeek === 'new'
                          ? t('insightsNew')
                          : card.weekOnWeek === 'unavailable'
                            ? '-'
                            : `${card.weekOnWeek} ${t('insightsVsLastWeek')}`}
                      </CardDetail>
                    </>
                  ) : null}
                </InsightCard>
              ))}
              {!expanded ? (
                <SummaryTile>
                  <CardLabel>{t('insightsUnusuallyOld')}</CardLabel>
                  <SummaryValue
                    title={
                      data?.unusuallyOldAvailable
                        ? undefined
                        : t('insightsUnavailable')
                    }
                  >
                    {data?.unusuallyOldAvailable ? data.unusuallyOldTotal : '-'}
                  </SummaryValue>
                </SummaryTile>
              ) : null}
            </CardGrid>
          )}

          {expanded && !data?.isEmpty ? (
            <Charts>
              <ChartBlock>
                <ChartHeader>
                  <strong>{t('insightsFlowTitle')}</strong>
                  <Segmented
                    size="small"
                    value={chartPeriodDays}
                    onChange={(value) =>
                      onPeriodChange(Number(value) as 7 | 28 | 90)
                    }
                    options={[
                      { label: '7', value: 7 },
                      { label: '28', value: 28 },
                      { label: '90', value: 90 },
                    ]}
                  />
                </ChartHeader>
                <ChartFrame>
                  <ReactECharts
                    option={flowOption}
                    style={{ height: '100%', width: '100%' }}
                  />
                </ChartFrame>
              </ChartBlock>
              <ChartBlock>
                <strong>{t('insightsAgeTitle')}</strong>
                <ChartFrame $spaced>
                  <ReactECharts
                    option={ageOption}
                    style={{ height: '100%', width: '100%' }}
                    onEvents={{
                      click: (params: {
                        componentType?: string;
                        seriesName?: string;
                        name?: string;
                        value?: unknown;
                      }) => {
                        const label = axisLabelName(params.value);
                        if (params.componentType === 'yAxis' && label) {
                          setHiddenAgeLabels((current) =>
                            toggleChartLabel(current, label),
                          );
                          return;
                        }
                        const card =
                          cards.find(
                            (item) => labelFor(item.key) === params.name,
                          )?.key ??
                          data?.ageChart?.find(
                            (row) => labelFor(row.cardKey) === params.name,
                          )?.cardKey;
                        const band = (
                          Object.keys(AGE_LABEL) as InspectionInsightAgeBand[]
                        ).find((key) => AGE_LABEL[key] === params.seriesName);
                        if (card && band) onSelectAgeBand(card, band);
                      },
                    }}
                  />
                </ChartFrame>
              </ChartBlock>
            </Charts>
          ) : null}
        </Spin>
      )}

      {drill.card ? (
        <DrillRow>
          <Space wrap>
            <Tag
              closeIcon
              onClose={onClearDrill}
              data-testid="insight-drill-chip"
            >
              {labelFor(drill.card)}
              {drill.ageBand ? ` · ${AGE_LABEL[drill.ageBand]}` : ''}
            </Tag>
            <Tooltip title={t('insightsDrillHint')}>
              <Hint>{t('insightsChipActive')}</Hint>
            </Tooltip>
          </Space>
        </DrillRow>
      ) : null}

      {emptyIntersection ? (
        <IntersectionAlert
          type="warning"
          showIcon
          message={t('insightsEmptyIntersection')}
        />
      ) : null}
    </InsightsPanel>
  );
};
